/**
 * The renderer's half of the prop catalogue: which model a sim `PropKind` gets. The sim's
 * half (footprints, durability) is src/sim/props.ts; the two share only the kind names.
 *
 * Both tables below are total over their kinds, so a new `PropKind` without a model is a
 * compile error here rather than a throw in the middle of loading a mission.
 */

import type { Pcg32 } from '../../sim/rng';
import type { PropDef, PropKind } from '../../sim/types';
import { hangar, samLauncher } from '../kit/airfield';
import { casemate, fieldGun, gunPit } from '../kit/artillery';
import { barge, bridgePier } from '../kit/boats';
import { barracks, commsPost, guardHouse, prison } from '../kit/buildings';
import { mobileLauncher, staffCar } from '../kit/convoy';
import { hedgeRun, snowDrift, stump, tankTrap } from '../kit/cover';
import { crashSite } from '../kit/crash';
import { alarmPost, ammoShack, magazine } from '../kit/depot';
import { campEmbers, culvert, observationPost, overlookMarker, partisanCamp } from '../kit/fieldMarks';
import {
  mannedTurret, proximityPost, searchlight, searchlightBase, searchlightHead, turretBunker,
} from '../kit/fieldworks';
import { broadleaf, cactus, deadTree, type KitBuilder, palm, pine, rock, shrub } from '../kit/flora';
import { fieldGrave } from '../kit/graves';
import { cellBlock, hqBuilding, safe } from '../kit/hq';
import { aircraft } from '../kit/jet';
import { laboratory, laboratoryGlow } from '../kit/laboratory';
import type { MeshBuilder } from '../kit/MeshBuilder';
import { bridgeRun, pipelineRun, pumpStation } from '../kit/pipeline';
import { controlsPyramid, lever, tigerPit } from '../kit/powCamp';
import { fuelWagon, locomotive, railRun } from '../kit/rail';
import { ruinHouse, ruinWallRun } from '../kit/ruins';
import {
  breakerLamp, breakerPanel, breakerPanelOff, strikeBeacon, strikeBeaconSpot, strikeBeaconStrobe, telephoneExchange,
} from '../kit/signals';
import {
  barrel, bunker, crate, fuelTank, hut, mgNest, radar, sandbagRun, tent, watchtower, wireRun,
} from '../kit/structures';
import { generatorHut, generatorHutOff, generatorLamp, relayMast, relayMastLamp } from '../kit/towers';
import { wallAlong, wallRun } from '../kit/walls';
import type { Palette } from '../palette';

/** Kinds whose model is built to `PropDef.length`, running along x. */
export type RunKind = 'wall' | 'wire' | 'sandbags' | 'hedge' | 'ruinWall' | 'pipeline' | 'bridge' | 'rail';
/**
 * `along` is where the piece sits along its own axis in the world (kit/walls.ts `wallAlong`): a builder that
 * spaces features out on a grid fixed to the world - the wall's pillars - keeps them from landing on every
 * joint of a run laid in short pieces. The others need not take it.
 */
type RunBuilder = (p: Palette, rng: Pcg32, length?: number, along?: number) => MeshBuilder;

/** Default length for each run kind when the def gives none: the same 4 m the sim's collider assumes. */
const RUNS: Record<RunKind, RunBuilder> = {
  wall: wallRun, wire: wireRun, sandbags: sandbagRun, hedge: hedgeRun,
  ruinWall: ruinWallRun, pipeline: pipelineRun, bridge: bridgeRun, rail: railRun,
};

const SIMPLE: Record<Exclude<PropKind, RunKind | 'rock'>, KitBuilder> = {
  pine, broadleaf, palm, deadTree, shrub, cactus, stump, snowDrift,
  bunker, mgNest, tankTrap, watchtower, searchlight, turretBunker, mannedTurret, proximityPost, alarmPost,
  hut, tent, guardHouse, barracks, ruinHouse, commsPost, hangar, prison,
  crate, barrel,
  fuelTank, ammoShack, magazine, radar, aircraft, samLauncher, pumpStation, tigerPit, controlsPyramid, lever,
  fieldGrave,
  // The expansion (10 §3). A kind with two states is listed here in the state a player would call "the thing".
  bridgePier, relayMast, casemate, generatorHut, locomotive, fuelWagon, mobileLauncher, barge,
  fieldGun, gunPit, staffCar, hqBuilding, cellBlock, laboratory, safe, telephoneExchange, crashSite,
  breakerPanel, overlookMarker, observationPost, partisanCamp, culvert, strikeBeacon,
};

/**
 * Kinds that can be switched. `on` is the model in `SIMPLE`; `off` is the builder here; `initial` is
 * the state a mission starts in: a breaker and a generator run until thrown, a strike beacon is only
 * a marked spot until the ranger sets it up there.
 */
export type PropSwitch = 'on' | 'off';
const SWITCHED: Partial<Record<PropKind, { off: KitBuilder; initial: PropSwitch }>> = {
  breakerPanel: { off: breakerPanelOff, initial: 'on' },
  generatorHut: { off: generatorHutOff, initial: 'on' },
  strikeBeacon: { off: strikeBeaconSpot, initial: 'off' },
};

/**
 * Self-lit parts (a lamp, a lit pane, embers): drawn by the world in an emissive material just proud
 * of the body, which carries the same part in the same colour - so the body alone is still the whole
 * model. Lit only while the kind is `on`.
 */
const GLOW: Partial<Record<PropKind, KitBuilder>> = {
  relayMast: relayMastLamp, generatorHut: generatorLamp, breakerPanel: breakerLamp,
  strikeBeacon: strikeBeaconStrobe, laboratory: laboratoryGlow, partisanCamp: campEmbers,
};

const isRun = (kind: PropKind): kind is RunKind => kind in RUNS;

/** True for kinds that honour `PropDef.length`. */
export function isRunKind(kind: PropKind): boolean {
  return isRun(kind);
}

/** The state a switched kind starts a mission in, or null for a kind with only one. */
export function initialSwitch(kind: PropKind): PropSwitch | null {
  return SWITCHED[kind]?.initial ?? null;
}

/** The whole model. `state` matters only to switched kinds, and defaults to `on` - the thing itself. */
export function buildPropModel(def: PropDef, palette: Palette, rng: Pcg32, state: PropSwitch = 'on'): MeshBuilder {
  if (def.kind === 'rock') return rock(palette, rng, { size: def.scale ?? 1 });
  if (isRun(def.kind)) return RUNS[def.kind](palette, rng, def.length ?? 4, wallAlong(def.x, def.z, def.yaw));
  const switched = SWITCHED[def.kind];
  return (state === 'off' && switched ? switched.off : SIMPLE[def.kind])(palette, rng);
}

/** The self-lit part of a kind (in the prop's own frame), or null: it has none, or it is switched off. */
export function buildPropGlow(
  def: PropDef, palette: Palette, rng: Pcg32, state: PropSwitch = 'on',
): MeshBuilder | null {
  const glow = GLOW[def.kind];
  return glow && state === 'on' ? glow(palette, rng) : null;
}

/**
 * Kinds with a part that turns (the searchlight's lamp): the world builds `body` into the prop's
 * own mesh and `head` as a separate object about the same origin, which a view may then rotate.
 * `buildPropModel` stays the whole thing, for the kit tests, the budgets and the turntable.
 */
export function buildPropParts(
  def: PropDef, palette: Palette, rng: Pcg32, state: PropSwitch = 'on',
): { body: MeshBuilder; head: MeshBuilder | null } {
  if (def.kind === 'searchlight') return { body: searchlightBase(palette, rng), head: searchlightHead(palette, rng) };
  return { body: buildPropModel(def, palette, rng, state), head: null };
}

/** Rocks take their size from the builder; everything else scales as a whole. */
export function propScale(def: PropDef): number {
  return def.kind === 'rock' ? 1 : (def.scale ?? 1);
}
